import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Chip,
  IconButton,
  FormHelperText,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Alert,
} from '@mui/material';
import {
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Send as SendIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Notifications as NotificationsIcon,
  Schedule as ScheduleIcon,
  PriorityHigh as PriorityIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  NotificationsActive as PushIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

function NotificationForm({
  notification = null,
  onSubmit,
  onCancel,
  open,
  onClose,
  nlpProvider = 'ollama',
  onNlpProviderChange,
}) {
  const [formData, setFormData] = useState({
    message: '',
    type: 'general',
    priority: 'normal',
    channel: 'in-app',
    related: '',
    time: null,
    read: false,
  });

  const [errors, setErrors] = useState({});
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [nlpEnabled, setNlpEnabled] = useState(false);
  const [nlpProcessing, setNlpProcessing] = useState(false);
  const [nlpResult, setNlpResult] = useState(null);

  useEffect(() => {
    if (notification) {
      setFormData({
        message: notification.message || '',
        type: notification.type || 'general',
        priority: notification.priority || 'normal',
        channel: notification.channel || 'in-app',
        related: notification.related || '',
        time: notification.time ? dayjs(notification.time) : null,
        read: notification.read || false,
      });
    } else {
      setFormData({
        message: '',
        type: 'general',
        priority: 'normal',
        channel: 'in-app',
        related: '',
        time: null,
        read: false,
      });
    }
    setErrors({});
    setNlpResult(null);
  }, [notification, open]);

  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      recognitionInstance.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => result.transcript)
          .join('');

        if (nlpEnabled) {
          processNlpInput(transcript);
        } else {
          setFormData((prev) => ({ ...prev, message: transcript }));
        }
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };

      recognitionInstance.onend = () => {
        setIsListening(false);
      };

      setRecognition(recognitionInstance);
    }
  }, [nlpEnabled]);

  const processNlpInput = async (transcript) => {
    setNlpProcessing(true);
    try {
      const response = await fetch('/api/v1/contacts/nlp-extract', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: transcript,
          provider: nlpProvider,
          entity: 'notification',
        }),
      });

      if (response.ok) {
        const result = await response.json();
        setNlpResult(result);

        // Auto-fill form fields based on NLP extraction
        if (result.extracted_data) {
          const extracted = result.extracted_data;
          setFormData((prev) => ({
            ...prev,
            message: extracted.message || prev.message,
            type: extracted.type || prev.type,
            priority: extracted.priority || prev.priority,
            channel: extracted.channel || prev.channel,
            related: extracted.related || prev.related,
          }));
        }
      }
    } catch (error) {
      console.error('NLP processing error:', error);
    } finally {
      setNlpProcessing(false);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognition?.stop();
    } else {
      recognition?.start();
      setIsListening(true);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    if (formData.message.length > 500) {
      newErrors.message = 'Message must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const submitData = {
        ...formData,
        time: formData.time ? formData.time.toISOString() : null,
      };
      onSubmit(submitData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const typeOptions = [
    { value: 'general', label: 'General', icon: NotificationsIcon },
    { value: 'info', label: 'Information', icon: NotificationsIcon },
    { value: 'warning', label: 'Warning', icon: NotificationsIcon },
    { value: 'error', label: 'Error', icon: NotificationsIcon },
    { value: 'success', label: 'Success', icon: NotificationsIcon },
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low' },
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ];

  const channelOptions = [
    { value: 'in-app', label: 'In-App', icon: NotificationsIcon },
    { value: 'email', label: 'Email', icon: EmailIcon },
    { value: 'sms', label: 'SMS', icon: SmsIcon },
    { value: 'push', label: 'Push', icon: PushIcon },
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {notification ? 'Edit Notification' : 'Create New Notification'}
      </Typography>

      {/* NLP Processing Result */}
      {nlpResult && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            NLP extracted:
            {' '}
            {nlpResult.extracted_data?.message || 'No data extracted'}
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Message Field with Voice Input */}
        <Grid item xs={12}>
          <Box sx={{ position: 'relative' }}>
            <TextField
              fullWidth
              label="Message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              multiline
              rows={3}
              error={!!errors.message}
              helperText={errors.message || `${formData.message.length}/500 characters`}
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <IconButton
                onClick={toggleListening}
                color={isListening ? 'error' : 'primary'}
                disabled={nlpProcessing}
              >
                {isListening ? <MicOffIcon /> : <MicIcon />}
              </IconButton>
              <Typography variant="caption" color="text.secondary">
                {isListening ? 'Listening...' : 'Click to start voice input'}
              </Typography>
              <FormControlLabel
                control={(
                  <Switch
                    checked={nlpEnabled}
                    onChange={(e) => setNlpEnabled(e.target.checked)}
                    size="small"
                  />
                )}
                label="NLP Processing"
              />
              {nlpEnabled && (
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={nlpProvider}
                    onChange={(e) => onNlpProviderChange(e.target.value)}
                    label="Provider"
                  >
                    <MenuItem value="ollama">Ollama (Llama 3)</MenuItem>
                    <MenuItem value="together">Together (Llama 4)</MenuItem>
                    <MenuItem value="openai">OpenAI</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
          </Box>
        </Grid>

        {/* Type and Priority */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.type}>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              label="Type"
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <option.icon fontSize="small" />
                    {option.label}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.priority}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
              label="Priority"
            >
              {priorityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <PriorityIcon fontSize="small" />
                    {option.label}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            {errors.priority && <FormHelperText>{errors.priority}</FormHelperText>}
          </FormControl>
        </Grid>

        {/* Channel and Related */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.channel}>
            <InputLabel>Channel</InputLabel>
            <Select
              value={formData.channel}
              onChange={(e) => handleInputChange('channel', e.target.value)}
              label="Channel"
            >
              {channelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <option.icon fontSize="small" />
                    {option.label}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
            {errors.channel && <FormHelperText>{errors.channel}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Related To"
            value={formData.related}
            onChange={(e) => handleInputChange('related', e.target.value)}
            placeholder="Contact, Deal, Task, etc."
            error={!!errors.related}
            helperText={errors.related}
          />
        </Grid>

        {/* Scheduled Time */}
        <Grid item xs={12}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Scheduled Time (Optional)"
              value={formData.time}
              onChange={(newValue) => handleInputChange('time', newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!!errors.time}
                  helperText={errors.time || 'Leave empty for immediate delivery'}
                />
              )}
              minDateTime={dayjs()}
            />
          </LocalizationProvider>
        </Grid>

        {/* Read Status */}
        <Grid item xs={12}>
          <FormControlLabel
            control={(
              <Switch
                checked={formData.read}
                onChange={(e) => handleInputChange('read', e.target.checked)}
              />
            )}
            label="Mark as Read"
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<CancelIcon />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={notification ? <SaveIcon /> : <SendIcon />}
          disabled={nlpProcessing}
        >
          {notification ? 'Update Notification' : 'Create Notification'}
        </Button>
      </Stack>
    </Box>
  );
}

export default NotificationForm;
